import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { OpenApiRequestStatus, useOpenApiRequest } from "typescript-request-utils";
import DatePicker, { registerLocale } from "react-datepicker";
import fi from "date-fns/locale/fi";
import { useSelector } from "react-redux";

import { ApplicationStatus } from "../../../api/enums/ApplicationStatus";
import { Relation } from "../../../api/enums/Relation";
import { Application } from "../../../api/models/Application";
import { UserRepresentation } from "../../../api/models/UserRepresentation";
import { getApiRegistry } from "../../state/apiRegistry";
import { LoadingContainer, ModalContent, ModalFieldValue, ModalFooter, ModalTitle, ModalWrapper } from "./styledComponents";
import { getCurrentUser } from "../../../features/basic/reducers/basicReducer";
import { BasicInput, PrimaryButton, SecondaryButton } from "../../../styles/basicComponents";
import { RepresentationRequestType } from "../../../api/enums/RepresentationRequestType";
import i18n from "../../../i18n/config";
import { useTheme, useVariables } from "../../../features/context/CustomerContext";
import { BackArrowIcon } from "./modalIcons";
import useCustomTranslation from "../../../features/hooks/CustomUseTranslation";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";

const ModalBody = styled.div`
    @media screen and (max-width: 470px) {
        width: 100%;
    }
    display: flex;
    flex-direction: column;
    align-self: center;
    position: relative;
`
const ModalSection = styled.div`
    @media screen and (max-width: 470px) {
        margin: 0;
    }
    display: flex;
    flex-direction: column;
    margin: auto;
    max-width: 412px;
`
const DateModalSection = styled.div`
    @media screen and (max-width: 470px) {
        flex-direction: column;
        margin: 0;
    }
    display: flex;
    margin: auto;
    flex-direction: initial;
`
const ModalField = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
`
const IntroductionTextSection = styled(ModalSection)`
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    padding-bottom: 20px;
    max-width: 412px;
    margin: auto;
`
const IntroductionText = styled(ModalField)`
    position: relative;
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
`
const InputTitle = styled.label`
    position: absolute;
    top: -7px;
    margin-left: 40px;
    background-color: white;
    padding: 0 10px 0 10px;
    color: #686868;
    z-index: 99;
`
const ModalInput = styled(BasicInput)`
    @media screen and (max-width: 470px) {
        width: 100%;
    }
    width: 412px;
`
const Error = styled.span`
    display: inline;
    padding:20px 0px 20px 0px;
`

const ErrorModalField = styled(ModalField)`
    margin-bottom: 5px;
`
const ErrorRed = styled.span`
    color:red;
`
const ErrorList = styled.li`
    padding-left:10px
`
const GivenInformation = styled.h4`
    font-weight: 500;
    display: inline;
    margin-right: 10px;
`
const GivenInformationTitle = styled(GivenInformation)`
    font-weight: 700;
`
const GivenInformationField = styled.div`
    display: block;
    margin-bottom: 10px;
    height: 22px;
`
const DatePickerWrapper = styled.div`
    display: inline-block;
    padding-right: 0;
    @media screen and (min-width: 471px) {
        padding-right: 100px;
        max-width: 100px;
    }
`

export interface NewApplicationModalProps {
    show: boolean;
    afterSave: () => void;
}

const NewApplicationModal: FC<NewApplicationModalProps> = (props) => {
    var currentDate = new Date();
    var maxYearsFromNow = new Date();
    const variables = useVariables();
    maxYearsFromNow.setFullYear(currentDate.getFullYear() + variables.AUTHORIZATION_MAX_PERIOD_OF_VALIDITY_IN_YEARS);

    registerLocale("fi", fi);

    const { t } = useCustomTranslation();
    const [applicationRequest, updateApplication] = useOpenApiRequest(getApiRegistry().applicationsApi.update);

    const [currentUser, setCurrentUser] = useState<UserRepresentation>({});
    const [requesteeUsername, setRequesteeUsername] = useState<string>('');
    const [requesteeCustomerNumber, setRequesteeCustomerNumber] = useState<string>('');
    const [startDate, setStartDate] = useState<Date>(currentDate);
    const [endDate, setEndDate] = useState<Date>(maxYearsFromNow);
    const [maximumDate, setMaximumDate] = useState<Date>(maxYearsFromNow);
    const [showError, setShowError] = useState<boolean>(false);
    const [ownUsernameError, setOwnUsernameError] = useState<boolean>(false);

    const currentUserFromState = useSelector(getCurrentUser);

    const theme = useTheme();

    useEffect(() => {
        if (currentUserFromState) {
            setCurrentUser(currentUserFromState);
        }
        // eslint-disable-next-line
    }, [currentUserFromState]);

    useEffect(() => {
        maximumDate.setDate(startDate.getDate());
        maximumDate.setMonth(startDate.getMonth());
        maximumDate.setFullYear(startDate.getFullYear() + variables.AUTHORIZATION_MAX_PERIOD_OF_VALIDITY_IN_YEARS);
        setMaximumDate(maximumDate);
        if (maximumDate < endDate) {
            endDate.setDate(maximumDate.getDate());
            endDate.setMonth(maximumDate.getMonth());
            endDate.setFullYear(maximumDate.getFullYear());
            setEndDate(endDate);
        }
        // eslint-disable-next-line
    }, [startDate]);

    useEffect(() => {
        if (!props.show) {
            setShowError(false);
            setDefaultValues();
        }
        // eslint-disable-next-line
    }, [props.show])

    useEffect(() => {
        if (applicationRequest.type === OpenApiRequestStatus.Complete) {
            if (applicationRequest.responsePayload.errorCode === "RR1000") {
                setShowError(true);
            }
            else {
                props.afterSave();
            }
        }
        // eslint-disable-next-line
    }, [applicationRequest])

    if (!props.show) {
        return null;
    }

    function handleAdd() {
        if (currentUser.username === requesteeUsername) {
            setOwnUsernameError(true);
        }
        else {
            const newApplication: Application = {
                requestorUser: currentUser,
                requesteeCustomerNumber: requesteeCustomerNumber,
                requesteeUsername: requesteeUsername,
                relation: Relation.REPRESENTATIVE,
                startDate: startDate,
                endDate: endDate,
                status: ApplicationStatus.PENDING,
                representationRequestType: RepresentationRequestType.USER_REQUEST
            };
            updateApplication(newApplication);
        }
    }

    function setDefaultValues() {
        setRequesteeCustomerNumber('');
        setRequesteeUsername('');
        setStartDate(currentDate);
        setEndDate(maxYearsFromNow);
        setOwnUsernameError(false);
    }

    function handleBack(){
        setShowError(false);
        setOwnUsernameError(false);
    }

    return (
        <ModalWrapper  theme={theme}>
            <ModalContent  theme={theme}>
                {!showError &&
                    <BackArrowIcon theme={theme} onClick={() => props.afterSave()} />}
                <ModalTitle theme={theme}>
                    {t("Request representation")}
                </ModalTitle>
                <ModalBody>
                    {showError ?
                        <ModalField>
                            <ErrorRed>{t("ErrorMsgSubtitle")}</ErrorRed>
                            <Error>{t("ErrorMsgFirst")}</Error>
                            <ErrorList>{t("ErrorMsgListFirst")}</ErrorList>
                            <ErrorList>{t("ErrorMsgListSecond")}</ErrorList>
                            <ErrorList>{t("ErrorMsgListThird")}</ErrorList>
                            <Error>{t("ErrorMsgCheckInformation")}</Error>
                            <GivenInformationField>
                                <GivenInformationTitle>{t("Receiver email")}:</GivenInformationTitle>
                                <GivenInformation>{requesteeUsername}</GivenInformation>
                            </GivenInformationField>
                            <GivenInformationField>
                                <GivenInformationTitle>{t("Requested customer")}:</GivenInformationTitle>
                                <GivenInformation>{requesteeCustomerNumber}</GivenInformation>
                            </GivenInformationField>
                            <GivenInformationField>
                                <GivenInformationTitle>{t("Start date")}:</GivenInformationTitle>
                                <GivenInformation>{startDate.toLocaleDateString("fi-FI")}</GivenInformation>
                            </GivenInformationField>
                            <GivenInformationField>
                                <GivenInformationTitle>{t("End date")}:</GivenInformationTitle>
                                <GivenInformation>{endDate.toLocaleDateString("fi-FI")}</GivenInformation>
                            </GivenInformationField>
                            <Error>{t("ErrorMsgSecond")}</Error>
                        </ModalField>
                        :
                        <>
                            {applicationRequest.type === OpenApiRequestStatus.Pending &&
                                <LoadingContainer theme={theme}>
                                <LoadingSpinner/>
                            </LoadingContainer>}
                            <IntroductionTextSection>
                                <IntroductionText> {t("New application introduction text first line")}</IntroductionText>
                                <IntroductionText> {t("New application introduction text second line")}</IntroductionText>
                            </IntroductionTextSection>
                            <ModalSection>
                                <ModalField>
                                    <InputTitle htmlFor="applicantInput">{t("Applicant")}</InputTitle>
                                    <ModalFieldValue>
                                        <ModalInput id="applicantInput" type="text" value={currentUser.username} disabled={true} />
                                    </ModalFieldValue>
                                </ModalField>
                                <ModalField>
                                    <InputTitle htmlFor="receiverEmailInput">{t("Receiver email")}</InputTitle>
                                    <ModalFieldValue>
                                        <ModalInput id="receiverEmailInput" type="text" value={requesteeUsername} onChange={(e) => setRequesteeUsername(e.target.value)} />
                                    </ModalFieldValue>
                                </ModalField>
                                <ModalField>
                                    <InputTitle htmlFor="requestedCustomerInput">{t("Requested customer")}</InputTitle>
                                    <ModalFieldValue>
                                        <ModalInput id="requestedCustomerInput" type="text" value={requesteeCustomerNumber} onChange={(e) => setRequesteeCustomerNumber(e.target.value)} />
                                    </ModalFieldValue>
                                </ModalField>

                                <DateModalSection>
                                    <ModalField>
                                        <InputTitle htmlFor="startDatePicker">{t("Start date")}</InputTitle>
                                        <DatePickerWrapper>
                                            <DatePicker id="startDatePicker" locale={i18n.language} minDate={currentDate} className={"modalDatePicker"} calendarStartDay={1} dateFormat={'dd.MM.yyyy'} selected={startDate} onChange={(date: Date | null) => date && setStartDate(date)} />
                                        </DatePickerWrapper>
                                    </ModalField>
                                    <ModalField>
                                        <InputTitle htmlFor="endDatePicker">{t("End date")}</InputTitle>
                                        <DatePickerWrapper>
                                            <DatePicker id="endDatePicker" locale={i18n.language} minDate={startDate} maxDate={maximumDate} className={"modalDatePicker"} calendarStartDay={1} dateFormat={'dd.MM.yyyy'} selected={endDate} onChange={(date: Date | null) => date && setEndDate(date)} />
                                        </DatePickerWrapper>
                                    </ModalField>
                                </DateModalSection>

                                <ErrorModalField>
                                    {(applicationRequest.type === OpenApiRequestStatus.NetworkError || applicationRequest.type === OpenApiRequestStatus.ServerError) ?
                                        <ErrorRed>{t("Error when trying to create application")}</ErrorRed> : <br />}
                                    {(ownUsernameError) ?
                                        <ErrorRed>{t("ErrorReceiverSenderSameEmail")}</ErrorRed> : <br />}
                                </ErrorModalField>
                            </ModalSection></>
                    }
                </ModalBody>
                {showError ?
                    <ModalFooter>
                        <SecondaryButton onClick={() => handleBack()}>{t("Back")}</SecondaryButton>
                        <PrimaryButton onClick={() => props.afterSave()}>{t("Close")}</PrimaryButton>
                    </ModalFooter>
                    :
                    <ModalFooter>
                        <SecondaryButton onClick={() => props.afterSave()}>{t("Back")}</SecondaryButton>
                        <PrimaryButton disabled={applicationRequest.type === OpenApiRequestStatus.Pending} onClick={() => handleAdd()}>{t("Create application")}</PrimaryButton>
                    </ModalFooter>}
            </ModalContent>
        </ModalWrapper>
    )
}

export default NewApplicationModal;